import { t } from '@framework/i18n/intl/i18next.ts';
import { yup } from '@framework/validation/initYup.ts';
import { RuleType } from '../../IRule.ts';
import { type IRuleType } from '../../IRuleType.ts';
import { ValueRangeRuleComponent } from './ValueRangeRuleComponent.tsx';

const configValidator = yup
  .object({
    min: yup.number().notRequired().label('Minimo'),
    minExclusive: yup.boolean().notRequired().label('Minimo escluso'),
    max: yup.number().notRequired().greaterThan('min').label('Massimo'),
    maxExclusive: yup.boolean().notRequired().label('Massimo escluso'),
    minCustomMessage: yup
      .string()
      .notRequired()
      .label('Messaggio personalizzato di validazione'),
    maxCustomMessage: yup
      .string()
      .notRequired()
      .label('Messaggio personalizzato di validazione'),
  })
  .notRequired()
  .noUnknown()
  .nullable();

type IRuleValueRangeType = yup.InferType<typeof configValidator>;

export const ValueRangeRule: IRuleType<IRuleValueRangeType> = {
  type: RuleType.ValueRange,
  name: 'Valore massimo',
  configComponent: ValueRangeRuleComponent,
  configValidator,
  validator: (schema, rule) => {
    if (!rule) return schema;
    const { min, max, minExclusive, maxExclusive } = rule;

    if (min != null) {
      const defaultMinMessage = minExclusive
        ? t(
            'rules.value-range.min-exclusive',
            'Il campo ${label} deve essere maggiore di {{min}}',
            { min },
          )
        : t(
            'rules.value-range.min',
            'Il campo ${label} deve essere maggiore o uguale a {{min}}',
            { min },
          );

      schema = schema.test({
        name: 'min-value',
        params: { min: rule.min },
        skipAbsent: true,
        message: rule.minCustomMessage ?? defaultMinMessage,
        test(value) {
          // Ci pensa la regola required a controllare l'esistenza del valore
          if (value == null) return true;
          if (minExclusive) return value > min;
          return value >= min;
        },
      });
    }

    if (max != null) {
      const defaultMaxMessage = maxExclusive
        ? t(
            'rules.value-range.max-exclusive',
            'Il campo ${label} deve essere minore di {{max}}',
            { max },
          )
        : t(
            'rules.value-range.max',
            'Il campo ${label} deve essere minore o uguale a {{max}}',
            { max },
          );

      schema = schema.test({
        name: 'max-value',
        params: { max: rule.max },
        skipAbsent: true,
        message: rule.maxCustomMessage ?? defaultMaxMessage,
        test(value) {
          // Ci pensa la regola required a controllare l'esistenza del valore
          if (value == null) return true;
          if (maxExclusive) return value < max;
          return value <= max;
        },
      });
    }

    return schema;
  },
};
